import { useState, useMemo, useEffect, useCallback } from "react";
import { ResetFiltersButton } from "../../common/ResetFiltersButton.tsx";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoIcon from "@mui/icons-material/Info";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { X, ChevronDown } from "lucide-react";
import svgPaths from "../../imports/svg-k6062vvkrz.ts";
import { useRecycleBinStore } from "../../store/recycleBinStore.ts";
import { applyRecycleBinTableTotalsToStore } from "../../utils/recycleBinTotalsSync.ts";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import type { RecycleBinItem } from "./RecycleBin.tsx";
import React from "react";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import {
  AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  AI_DETECTION_ROWS_PER_PAGE_OPTIONS,
} from "../../constants/aiDetectionPagination";

declare const process: { env: Record<string, string | undefined> };
const USE_CASE_CONFIG_API_BASE =
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** Room for MUI chevron (~32px) + gap + clear chip (28px) so label does not run under the X. */
const RECYCLE_BIN_FILTER_SELECT_PR_CLEAR = "76px";
const RECYCLE_BIN_FILTER_CLEAR_BTN_RIGHT_PX = 36;

/** GET /recycle-bin response: tables with total and items per table */
export interface RecycleBinTableData {
  total: number;
  items: Record<string, unknown>[];
}
export interface RecycleBinApiResponse {
  tables: Record<string, RecycleBinTableData>;
}

/** Wrapper response: { message, status, data: { tables } } */
export interface RecycleBinApiWrapper {
  message?: string;
  status?: string;
  data?: { tables?: Record<string, RecycleBinTableData> };
  tables?: Record<string, RecycleBinTableData>;
}

/** Map API table key to display type */
function tableKeyToType(tableKey: string): RecycleBinTableType | "face" {
  const map: Record<string, RecycleBinTableType | "face"> = {
    zones: "zone",
    face_enrollment: "face",
    vehicle_enrollment: "vehicle_enrollment",
    detections: "detection",
    camera_ai_configs: "camera_ai_config",
  };
  return map[tableKey] ?? "zone";
}

function getItemDisplayName(
  tableKey: string,
  item: Record<string, unknown>,
): string {
  if (tableKey === "camera_ai_configs") {
    const formatted = getCameraAiConfigRecycleDisplayName(item);
    if (formatted) return formatted;
  }
  const name = item.name as string | undefined;
  if (name != null && name !== "") return name;
  if (tableKey === "vehicle_enrollment") {
    const lp = item.license_plate as string | undefined;
    const owner = item.vehicle_owner as string | undefined;
    if (lp) return lp;
    if (owner) return owner;
  }
  const id = item.id as string | undefined;
  return id ?? "—";
}

function getItemDeletedAt(item: Record<string, unknown>): Date {
  const d = item.deleted_at as string | undefined;
  return d ? new Date(d) : new Date();
}

/** First non-empty string among known keys on a record (API may use snake_case or camelCase). */
function firstStringField(
  record: Record<string, unknown>,
  keys: readonly string[],
): string {
  for (const k of keys) {
    const v = record[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/**
 * Recycle-bin row from `camera_ai_configs`: prefer API `item_name`, else
 * `Zone name (Use case name)` from nested fields.
 */
function getCameraAiConfigRecycleDisplayName(
  item: Record<string, unknown>,
): string | null {
  let flat: Record<string, unknown> = { ...item };
  for (const key of ["data", "camera_ai_config"] as const) {
    const v = flat[key];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      flat = { ...flat, ...(v as Record<string, unknown>) };
    }
  }
  const fromItemName = firstStringField(flat, ["item_name", "itemName"]);
  if (fromItemName) return fromItemName;
  const zone = firstStringField(flat, [
    "zone_name",
    "zoneName",
    "zone_title",
    "zoneTitle",
  ]);
  let useCasePart = firstStringField(flat, [
    "use_case_name",
    "useCaseName",
    "use_case",
    "useCase",
  ]);
  if (!useCasePart) {
    const arr = flat.use_cases;
    if (Array.isArray(arr) && arr.length > 0) {
      const names = arr
        .map((uc) => {
          if (!uc || typeof uc !== "object") return "";
          const r = uc as Record<string, unknown>;
          return firstStringField(r, ["use_case_name", "useCaseName", "name"]);
        })
        .filter(Boolean);
      if (names.length > 0) useCasePart = names.join(", ");
    }
  }
  if (zone && useCasePart) return `${zone} (${useCasePart})`;
  if (zone) return zone;
  if (useCasePart) return useCasePart;
  return null;
}

/**
 * Original / source path for recycle-bin rows: prefer explicit original_* fields, then path-like API keys.
 */
function deriveOriginalLocationFromRaw(
  tableKey: string,
  raw: Record<string, unknown>,
): string {
  const fromOriginal = firstStringField(raw, [
    "original_location",
    "originalLocation",
    "original_path",
    "originalPath",
    "origin_path",
    "originPath",
  ]);
  if (fromOriginal) return fromOriginal;
  const fromMeta = firstStringField(raw, [
    "file_location",
    "fileLocation",
    "file_path",
    "filePath",
    "path",
    "storage_path",
    "storagePath",
    "directory",
    "image_path",
    "imagePath",
    "preview_path",
    "previewPath",
    "source_path",
    "sourcePath",
    "drawing_path",
    "drawingPath",
  ]);
  if (fromMeta) return fromMeta;
  if (tableKey === "face_enrollment") {
    return firstStringField(raw, [
      "front",
      "left_profile",
      "right_profile",
      "up_face",
      "down_face",
      "front_url",
      "left_profile_url",
      "right_profile_url",
      "up_face_url",
      "down_face_url",
    ]);
  }
  return "";
}

const TABLE_HEADER_STYLE = {
  bgcolor: "#F0F0F0",
  fontFamily: "Roboto, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  color: "#2932E5",
  borderBottom: "1px solid #E5E7EB",
  px: "10px",
  py: "10px",
  verticalAlign: "middle" as const,
  textAlign: "left" as const,
};
const TYPE_BADGE_STYLE = {
  bgcolor: "#FFE0D0",
  color: "#E65100",
  fontFamily: "Roboto, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  height: 24,
  borderRadius: "12px",
  textAlign: "left" as const,
};
const DAYS_BADGE_STYLE = {
  bgcolor: "#E8F5E9",
  color: "#2E7D32",
  fontFamily: "Roboto, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  height: 24,
  borderRadius: "12px",
  textAlign: "left" as const,
};

/** API response item from GET /use-case-configuration/faces/recycle-bin */
export interface FaceRecycleBinItem {
  id: string;
  name?: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  expiration_date?: string;
  company?: string;
  description?: string;
  front?: string;
  left_profile?: string;
  right_profile?: string;
  up_face?: string;
  down_face?: string;
  front_url?: string;
  left_profile_url?: string;
  right_profile_url?: string;
  up_face_url?: string;
  down_face_url?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

/** API table key → display type (for filter and label) */
export const RECYCLE_BIN_TABLE_TYPES = [
  "zone",
  "face",
  "vehicle_enrollment",
  "detection",
  "camera_ai_config",
] as const;
export type RecycleBinTableType = (typeof RECYCLE_BIN_TABLE_TYPES)[number];

/** Unified row for table: from store, face (API), or other API tables */
export type RecycleBinRowItem =
  | {
      source: "store";
      /** Stable key for selection (table + id). */
      rowKey: string;
      id: string;
      name: string;
      type: string;
      deletedAt: Date;
      expiresAt: Date;
      description?: string;
      /** Resolved from store payload when present. */
      originalLocation?: string;
      storeItem: RecycleBinItem;
    }
  | {
      source: "face";
      rowKey: string;
      id: string;
      name: string;
      type: "face";
      deletedAt: Date;
      expiresAt: Date;
      description?: string;
      originalLocation?: string;
      faceItem: FaceRecycleBinItem;
    }
  | {
      source: "api";
      rowKey: string;
      id: string;
      name: string;
      type: RecycleBinTableType;
      deletedAt: Date;
      expiresAt: Date;
      description?: string;
      originalLocation?: string;
      rawItem: Record<string, unknown>;
      tableKey: string;
    };

type Order = "asc" | "desc";
type OrderBy = "name" | "originalLocation" | "type" | "deletedAt" | "expiresAt";

/** Deleted-at presets; values match client-side filter logic in {@link RecycleBinTab}. */
const RECYCLE_BIN_DELETED_DATE_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All deleted items" },
  { value: "last-7", label: "Last 7 days" },
  { value: "last-30", label: "Last 30 days" },
  { value: "older", label: "Older than 30 days" },
];

const RECYCLE_BIN_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Types" },
  { value: "zone", label: "Zone" },
  { value: "face", label: "Face Enrollment" },
  { value: "vehicle_enrollment", label: "Vehicle Enrollment" },
  { value: "camera_ai_config", label: "AI model" },
];

const RECYCLE_BIN_EXPIRY_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Expiry" },
  { value: "expiring-soon", label: "≤ 7 Days" },
  { value: "8-30", label: "8-30 Days" },
  { value: "31-60", label: "31-60 Days" },
];

function CalendarToolbarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 15 16.5" fill="none" aria-hidden>
      <path d={svgPaths.p25b8fc00} fill="#4A5565" />
    </svg>
  );
}

/**
 * Native &lt;select&gt; + overlay icons (matches AI dashboard table date control).
 * MUI Select + endAdornment clear button was stealing clicks on the dropdown arrow.
 */
function RecycleBinToolbarNativeSelect({
  value,
  onChange,
  defaultValue,
  options,
  ariaLabel,
  title,
  clearAriaLabel,
  clearTitle,
  minWidth = 172,
  leftIcon,
}: {
  value: string;
  onChange: (next: string) => void;
  defaultValue: string;
  options: { value: string; label: string }[];
  ariaLabel: string;
  title?: string;
  clearAriaLabel: string;
  clearTitle: string;
  minWidth?: number;
  leftIcon: React.ReactNode;
}) {
  const isDefault = value === defaultValue;
  const showClear = !isDefault;
  const bgColor = isDefault ? "#fff" : "#fff4e6";
  const borderColor = isDefault ? "#e5e7eb" : "#e8c4a0";
  const selectPaddingRight = showClear ? 68 : 28;

  return (
    <div style={{ position: "relative" }}>
      <span
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
        }}
        aria-hidden
      >
        {leftIcon}
      </span>
      <select
        aria-label={ariaLabel}
        title={title}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          backgroundColor: bgColor,
          height: 36,
          minWidth,
          borderRadius: 10,
          border: `1px solid ${borderColor}`,
          paddingLeft: 32,
          paddingRight: selectPaddingRight,
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          color: "#1e3a5f",
          cursor: "pointer",
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {showClear && (
        <button
          type="button"
          aria-label={clearAriaLabel}
          title={clearTitle}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange(defaultValue);
          }}
          style={{
            position: "absolute",
            right: 32,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            padding: 0,
            margin: 0,
            border: "none",
            borderRadius: 6,
            background: "transparent",
            color: "#4a5565",
            cursor: "pointer",
          }}
        >
          <X size={16} strokeWidth={2.25} aria-hidden />
        </button>
      )}
      <span
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <ChevronDown size={18} style={{ color: "#4a5565" }} aria-hidden />
      </span>
    </div>
  );
}

function RecycleBinDeletedDateToolbarSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <RecycleBinToolbarNativeSelect
      value={value}
      onChange={onChange}
      defaultValue="all"
      options={RECYCLE_BIN_DELETED_DATE_OPTIONS}
      minWidth={178}
      ariaLabel="Deleted date range"
      title="Filter by when items were moved to the recycle bin"
      clearAriaLabel="Reset deleted date filter to all items"
      clearTitle="Show all deleted items"
      leftIcon={<CalendarToolbarIcon />}
    />
  );
}

export function RecycleBinTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  );
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<OrderBy>("deletedAt");
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);

  // Filter states
  const [filterType, setFilterType] = useState<string>("all");
  const [filterExpiryRange, setFilterExpiryRange] = useState<string>("all");
  const [filterDateRange, setFilterDateRange] = useState<string>("all");

  /** "Detection" is not offered as a type filter; normalize legacy state. */
  useEffect(() => {
    if (filterType === "detection") {
      setFilterType("all");
    }
  }, [filterType]);

  /** Context menu at cursor (right-click) or anchored to ⋮ button */
  const [rowActionMenu, setRowActionMenu] = useState<
    | {
        anchor: "cursor";
        top: number;
        left: number;
        rowItem: RecycleBinRowItem;
      }
    | {
        anchor: "element";
        el: HTMLElement;
        rowItem: RecycleBinRowItem;
      }
    | null
  >(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "restore" | "delete" | "empty" | "bulkRestore" | "bulkDelete";
    rowItem?: RecycleBinRowItem;
    bulkRows?: RecycleBinRowItem[];
  }>({ open: false, type: "restore" });
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [restoreFaceLoading, setRestoreFaceLoading] = useState(false);
  const [deleteFaceLoading, setDeleteFaceLoading] = useState(false);
  const [emptyBinLoading, setEmptyBinLoading] = useState(false);

  // Recycle bin store
  const items = useRecycleBinStore((state) => state.items);
  const setFacesRecycleBinCount = useRecycleBinStore(
    (state) => state.setFacesRecycleBinCount,
  );
  const setRecycleBinTableTotals = useRecycleBinStore(
    (state) => state.setRecycleBinTableTotals,
  );
  const restoreItem = useRecycleBinStore((state) => state.restoreItem);
  const permanentlyDeleteItem = useRecycleBinStore(
    (state) => state.permanentlyDeleteItem,
  );
  const emptyBin = useRecycleBinStore((state) => state.emptyBin);

  // GET /recycle-bin (all tables with is_deleted) – called when tab is shown
  const [recycleBinData, setRecycleBinData] =
    useState<RecycleBinApiResponse | null>(null);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);
  const [recycleBinError, setRecycleBinError] = useState<string | null>(null);

  const fetchRecycleBin = useCallback(() => {
    const base = USE_CASE_CONFIG_API_BASE.replace(/\/$/, "");
    if (!base) {
      setRecycleBinError("API base URL is not configured.");
      return;
    }
    const url = `${base}/recycle-bin`;
    setRecycleBinLoading(true);
    setRecycleBinError(null);
    fetch(url, {
      method: "GET",
      headers: { Accept: "application/json", ...userTimezoneHeaders() },
    })
      .then(async (res) => {
        const text = await res.text();
        const trimmed = text.trim();
        if (!res.ok) {
          let message = res.statusText || `HTTP ${res.status}`;
          if (trimmed.startsWith("{")) {
            try {
              const json = JSON.parse(text) as {
                detail?: string | Array<{ msg?: string }>;
                message?: string;
              };
              if (typeof json.detail === "string") message = json.detail;
              else if (Array.isArray(json.detail) && json.detail.length > 0)
                message =
                  json.detail
                    .map((d) => d.msg)
                    .filter(Boolean)
                    .join("; ") || message;
              else if (json.message) message = json.message;
            } catch {
              if (trimmed.length < 200) message = trimmed;
            }
          }
          throw new Error(message);
        }
        if (trimmed.startsWith("<")) {
          throw new Error(
            "Server returned HTML instead of JSON. Check that the API is reachable.",
          );
        }
        const body: RecycleBinApiWrapper = trimmed ? JSON.parse(text) : {};
        const tables =
          body?.data && typeof body.data.tables === "object"
            ? body.data.tables
            : body?.tables && typeof body.tables === "object"
              ? body.tables
              : {};
        return { tables };
      })
      .then((payload: RecycleBinApiResponse) => {
        const tables = payload?.tables ?? {};
        setRecycleBinData(payload?.tables ? payload : { tables: {} });
        applyRecycleBinTableTotalsToStore(tables);
      })
      .catch((err: unknown) => {
        setRecycleBinError(
          err instanceof Error ? err.message : "Failed to load recycle bin",
        );
        setRecycleBinData(null);
        setFacesRecycleBinCount(0);
        setRecycleBinTableTotals({});
      })
      .finally(() => {
        setRecycleBinLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchRecycleBin();
  }, [fetchRecycleBin]);

  /** POST /recycle-bin/restore-selected — restore by recycle-bin row UUIDs (any table). */
  const restoreRecycleBinSelectedApi = useCallback(
    async (ids: string[]): Promise<{ affected: number }> => {
      const base = USE_CASE_CONFIG_API_BASE.replace(/\/$/, "");
      if (!base) throw new Error("API base URL is not configured.");
      const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
      if (unique.length === 0) return { affected: 0 };
      const url = `${base}/recycle-bin/restore-selected`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify({ ids: unique }),
      });
      const text = await res.text();
      let parsed: {
        affected?: number;
        detail?: string | Array<{ msg?: string }>;
        message?: string;
      } = {};
      if (text.trim()) {
        try {
          parsed = JSON.parse(text) as typeof parsed;
        } catch {
          /* ignore */
        }
      }
      if (!res.ok) {
        let msg = res.statusText || `HTTP ${res.status}`;
        if (typeof parsed.message === "string" && parsed.message.trim()) {
          msg = parsed.message;
        } else if (typeof parsed.detail === "string") {
          msg = parsed.detail;
        } else if (Array.isArray(parsed.detail)) {
          const parts = parsed.detail
            .map((d) => d?.msg)
            .filter(Boolean) as string[];
          if (parts.length) msg = parts.join("; ");
        } else if (
          text.trim() &&
          !text.trim().startsWith("<") &&
          text.length < 400
        ) {
          msg = text.trim();
        }
        throw new Error(msg);
      }
      if (text.trim().startsWith("<")) {
        throw new Error("Server returned HTML instead of JSON.");
      }
      return {
        affected: typeof parsed.affected === "number" ? parsed.affected : 0,
      };
    },
    [],
  );

  /** POST /recycle-bin/permanent-delete-selected — permanently delete by recycle-bin row UUIDs (any table). */
  const permanentDeleteRecycleBinSelectedApi = useCallback(
    async (ids: string[]): Promise<{ affected: number }> => {
      const base = USE_CASE_CONFIG_API_BASE.replace(/\/$/, "");
      if (!base) throw new Error("API base URL is not configured.");
      const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
      if (unique.length === 0) return { affected: 0 };
      const url = `${base}/recycle-bin/permanent-delete-selected`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify({ ids: unique }),
      });
      const text = await res.text();
      let parsed: {
        affected?: number;
        detail?: string | Array<{ msg?: string }>;
        message?: string;
      } = {};
      if (text.trim()) {
        try {
          parsed = JSON.parse(text) as typeof parsed;
        } catch {
          /* ignore */
        }
      }
      if (!res.ok) {
        let msg = res.statusText || `HTTP ${res.status}`;
        if (typeof parsed.message === "string" && parsed.message.trim()) {
          msg = parsed.message;
        } else if (typeof parsed.detail === "string") {
          msg = parsed.detail;
        } else if (Array.isArray(parsed.detail)) {
          const parts = parsed.detail
            .map((d) => d?.msg)
            .filter(Boolean) as string[];
          if (parts.length) msg = parts.join("; ");
        } else if (
          text.trim() &&
          !text.trim().startsWith("<") &&
          text.length < 400
        ) {
          msg = text.trim();
        }
        throw new Error(msg);
      }
      if (text.trim().startsWith("<")) {
        throw new Error("Server returned HTML instead of JSON.");
      }
      return {
        affected: typeof parsed.affected === "number" ? parsed.affected : 0,
      };
    },
    [],
  );

  /** DELETE /recycle-bin – permanently delete all items. Optional ?table= for single table; omit for all. */
  const deleteAllRecycleBinApi = useCallback(
    async (table?: string): Promise<{ tables?: Record<string, number> }> => {
      const base = USE_CASE_CONFIG_API_BASE.replace(/\/$/, "");
      if (!base) throw new Error("API base URL is not configured.");
      const url =
        table && table !== "all"
          ? `${base}/recycle-bin?table=${encodeURIComponent(table)}`
          : `${base}/recycle-bin`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      if (!res.ok) throw new Error(res.statusText || String(res.status));
      const text = await res.text();
      if (!text.trim()) return {};
      if (text.trim().startsWith("<"))
        throw new Error("Server returned HTML instead of JSON.");
      return JSON.parse(text);
    },
    [],
  );

  const runRestoreForRows = useCallback(
    async (rows: RecycleBinRowItem[]) => {
      if (rows.length === 0) return;
      setRestoreFaceLoading(true);
      setRecycleBinError(null);
      try {
        const apiIds: string[] = [];
        for (const row of rows) {
          if (row.source === "store") {
            restoreItem(row.storeItem.id);
            continue;
          }
          if (row.source === "face") {
            const id =
              (row.faceItem.id && String(row.faceItem.id).trim()) ||
              row.id.trim();
            if (id) apiIds.push(id);
            continue;
          }
          if (row.source === "api" && row.id.trim()) {
            apiIds.push(row.id.trim());
          }
        }
        const uniqueIds = [...new Set(apiIds)];
        if (uniqueIds.length > 0) {
          await restoreRecycleBinSelectedApi(uniqueIds);
          fetchRecycleBin();
        }
      } catch (e) {
        setRecycleBinError(
          e instanceof Error
            ? e.message
            : "Failed to restore one or more items.",
        );
      } finally {
        setRestoreFaceLoading(false);
      }
    },
    [restoreItem, restoreRecycleBinSelectedApi, fetchRecycleBin],
  );

  const runPermanentDeleteForRows = useCallback(
    async (rows: RecycleBinRowItem[]) => {
      if (rows.length === 0) return;
      setDeleteFaceLoading(true);
      setRecycleBinError(null);
      try {
        const apiIds: string[] = [];
        for (const row of rows) {
          if (row.source === "store") {
            permanentlyDeleteItem(row.storeItem.id);
            continue;
          }
          if (row.source === "face") {
            const id =
              (row.faceItem.id && String(row.faceItem.id).trim()) ||
              row.id.trim();
            if (id) apiIds.push(id);
            continue;
          }
          if (row.source === "api" && row.id.trim()) {
            apiIds.push(row.id.trim());
          }
        }
        const uniqueIds = [...new Set(apiIds)];
        if (uniqueIds.length > 0) {
          await permanentDeleteRecycleBinSelectedApi(uniqueIds);
          fetchRecycleBin();
        }
      } catch (e) {
        setRecycleBinError(
          e instanceof Error
            ? e.message
            : "Failed to permanently delete one or more items.",
        );
      } finally {
        setDeleteFaceLoading(false);
      }
    },
    [
      permanentlyDeleteItem,
      permanentDeleteRecycleBinSelectedApi,
      fetchRecycleBin,
    ],
  );

  const handleRowMenuOpenFromButton = (
    event: React.MouseEvent<HTMLElement>,
    rowItem: RecycleBinRowItem,
  ) => {
    setRowActionMenu({
      anchor: "element",
      el: event.currentTarget,
      rowItem,
    });
  };

  const handleRowMenuClose = () => {
    setRowActionMenu(null);
  };

  const handleRestoreClick = (rowItem?: RecycleBinRowItem) => {
    const toRestore = rowItem ?? rowActionMenu?.rowItem;
    if (toRestore) {
      setConfirmDialog({ open: true, type: "restore", rowItem: toRestore });
    }
    handleRowMenuClose();
  };

  const handleDeleteClick = (rowItem?: RecycleBinRowItem) => {
    const toDelete = rowItem ?? rowActionMenu?.rowItem;
    if (toDelete) {
      setConfirmDialog({ open: true, type: "delete", rowItem: toDelete });
    }
    handleRowMenuClose();
  };

  const handleEmptyBinClick = () => {
    setConfirmDialog({ open: true, type: "empty" });
  };

  const handleConfirmAction = async () => {
    if (confirmDialog.type === "restore" && confirmDialog.rowItem) {
      await runRestoreForRows([confirmDialog.rowItem]);
    } else if (
      confirmDialog.type === "bulkRestore" &&
      confirmDialog.bulkRows?.length
    ) {
      await runRestoreForRows(confirmDialog.bulkRows);
      setSelectedRowKeys([]);
    } else if (confirmDialog.type === "delete" && confirmDialog.rowItem) {
      await runPermanentDeleteForRows([confirmDialog.rowItem]);
    } else if (
      confirmDialog.type === "bulkDelete" &&
      confirmDialog.bulkRows?.length
    ) {
      await runPermanentDeleteForRows(confirmDialog.bulkRows);
      setSelectedRowKeys([]);
    } else if (confirmDialog.type === "empty") {
      setEmptyBinLoading(true);
      setRecycleBinError(null);
      try {
        await deleteAllRecycleBinApi();
        emptyBin();
        fetchRecycleBin();
      } catch (_e) {
        setRecycleBinError("Failed to empty recycle bin.");
      } finally {
        setEmptyBinLoading(false);
      }
    }
    setConfirmDialog({ open: false, type: "restore", bulkRows: undefined });
  };

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      zone: "Zone",
      face: "Face Enrollment",
      vehicle_enrollment: "Vehicle Enrollment",
      detection: "Detection",
      camera_ai_config: "AI model",
      "ai-model": "AI model",
      camera: "Camera",
      user: "User",
      "use-case": "Use Case",
      other: "Other",
    };
    return labels[type] || type || "Unknown";
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      zone: "#FF8555",
      face: "#2196F3",
      vehicle_enrollment: "#795548",
      detection: "#009688",
      camera_ai_config: "#2932E5",
      "ai-model": "#2932E5",
      camera: "#34A853",
      user: "#9C27B0",
      "use-case": "#FF6F00",
      other: "#757575",
    };
    return colors[type] || "#757575";
  };

  /** Unified rows from GET /recycle-bin data.tables (zones, face_enrollment, vehicle_enrollment, detections, camera_ai_configs). */
  const allRows = useMemo((): RecycleBinRowItem[] => {
    const tables = recycleBinData?.tables ?? {};
    const rows: RecycleBinRowItem[] = [];
    for (const [tableKey, tableData] of Object.entries(tables)) {
      const items = Array.isArray(tableData?.items) ? tableData.items : [];
      const type = tableKeyToType(tableKey);
      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        const raw = item as Record<string, unknown>;
        const id = (raw.id as string) ?? "";
        const rowKey = `${tableKey}:${id || `row-${idx}`}`;
        const deletedAt = getItemDeletedAt(raw);
        const expiresAt = new Date(deletedAt);
        expiresAt.setDate(expiresAt.getDate() + 60);
        const name = getItemDisplayName(tableKey, raw);
        const originalLocation = deriveOriginalLocationFromRaw(tableKey, raw);
        if (tableKey === "face_enrollment") {
          rows.push({
            source: "face",
            rowKey,
            id,
            name: (raw.name as string) ?? name,
            type: "face",
            deletedAt,
            expiresAt,
            description: (raw.description as string) ?? undefined,
            originalLocation: originalLocation || undefined,
            faceItem: raw as unknown as FaceRecycleBinItem,
          });
        } else {
          rows.push({
            source: "api",
            rowKey,
            id,
            name,
            type: type as RecycleBinTableType,
            deletedAt,
            expiresAt,
            description: (raw.description as string) ?? undefined,
            originalLocation: originalLocation || undefined,
            rawItem: raw,
            tableKey,
          });
        }
      }
    }
    return rows;
  }, [recycleBinData]);

  useEffect(() => {
    const valid = new Set(allRows.map((r) => r.rowKey));
    setSelectedRowKeys((prev) => {
      const next = prev.filter((k) => valid.has(k));
      return next.length === prev.length ? prev : next;
    });
  }, [allRows]);

  const getDaysRemaining = (expiresAt: Date) => {
    const now = new Date();
    const diff = expiresAt.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  // Filter and sort items (store + face rows)
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...allRows];

    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.name.toLowerCase().includes(q) ||
          row.description?.toLowerCase().includes(q) ||
          (row.originalLocation ?? "").toLowerCase().includes(q) ||
          getTypeLabel(row.type).toLowerCase().includes(q),
      );
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((row) => row.type === filterType);
    }

    // Expiry range filter
    if (filterExpiryRange !== "all") {
      filtered = filtered.filter((row) => {
        const daysRemaining = getDaysRemaining(row.expiresAt);
        if (filterExpiryRange === "expiring-soon") {
          return daysRemaining <= 7;
        } else if (filterExpiryRange === "8-30") {
          return daysRemaining > 7 && daysRemaining <= 30;
        } else if (filterExpiryRange === "31-60") {
          return daysRemaining > 30;
        }
        return true;
      });
    }

    // Date range filter
    if (filterDateRange !== "all") {
      const now = new Date();
      filtered = filtered.filter((row) => {
        const deletedDate = row.deletedAt.getTime();
        if (filterDateRange === "last-7") {
          return deletedDate >= now.getTime() - 7 * 24 * 60 * 60 * 1000;
        } else if (filterDateRange === "last-30") {
          return deletedDate >= now.getTime() - 30 * 24 * 60 * 60 * 1000;
        } else if (filterDateRange === "older") {
          return deletedDate < now.getTime() - 30 * 24 * 60 * 60 * 1000;
        }
        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (orderBy === "name") {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (orderBy === "originalLocation") {
        aValue = (a.originalLocation ?? "").toLowerCase();
        bValue = (b.originalLocation ?? "").toLowerCase();
      } else if (orderBy === "type") {
        aValue = getTypeLabel(a.type).toLowerCase();
        bValue = getTypeLabel(b.type).toLowerCase();
      } else if (orderBy === "deletedAt") {
        aValue = a.deletedAt.getTime();
        bValue = b.deletedAt.getTime();
      } else {
        aValue = getDaysRemaining(a.expiresAt);
        bValue = getDaysRemaining(b.expiresAt);
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    allRows,
    searchTerm,
    filterType,
    filterExpiryRange,
    filterDateRange,
    order,
    orderBy,
  ]);

  const hasActiveFilters =
    Boolean(searchTerm.trim()) ||
    filterType !== "all" ||
    filterExpiryRange !== "all" ||
    filterDateRange !== "all";

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterExpiryRange("all");
    setFilterDateRange("all");
    setPage(0);
  };

  const paginatedItems = useMemo(() => {
    return filteredAndSortedItems.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredAndSortedItems, page, rowsPerPage]);

  const selectedKeySet = useMemo(
    () => new Set(selectedRowKeys),
    [selectedRowKeys],
  );

  const clearRowSelection = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  const toggleRowKey = useCallback((key: string) => {
    setSelectedRowKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const handleBulkToolbarRestore = useCallback(() => {
    const rows = allRows.filter((r) => selectedKeySet.has(r.rowKey));
    if (rows.length === 0) return;
    setConfirmDialog({
      open: true,
      type: "bulkRestore",
      bulkRows: rows,
    });
  }, [allRows, selectedKeySet]);

  const handleBulkToolbarDelete = useCallback(() => {
    const rows = allRows.filter((r) => selectedKeySet.has(r.rowKey));
    if (rows.length === 0) return;
    setConfirmDialog({
      open: true,
      type: "bulkDelete",
      bulkRows: rows,
    });
  }, [allRows, selectedKeySet]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "800px",
          bgcolor: "#fff",
          borderRadius: "10px",
          border: "1px solid #E5E7EB",
        }}
      >
        {/* Header with Title and Info Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 2,
            pb: 1.5,
            height: "50px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 400,
                color: "#FF8555",
              }}
            >
              Recycle Bin
            </Typography>
            <Tooltip
              title="Items in the recycle bin will be automatically deleted after 60 days. You can restore or permanently delete them before that."
              {...GLOBAL_TOOLTIP_PROPS}
            >
              <IconButton
                size="small"
                sx={{
                  color: "#9E9E9E",
                  padding: "2px",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <InfoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Error / Loading / Content – same pattern as Manage Use Case */}
        {recycleBinError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              minHeight: 320,
              bgcolor: "#FAFAFA",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "#B71C1C" }} />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#4A5565",
                textAlign: "center",
              }}
            >
              {recycleBinError}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchRecycleBin}
              sx={{
                bgcolor: "#2932E5",
                textTransform: "none",
                "&:hover": { bgcolor: "#1f28b8" },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : recycleBinLoading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              minHeight: 320,
              bgcolor: "#FAFAFA",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#6B7280",
              }}
            >
              Loading recycle bin...
            </Typography>
          </Box>
        ) : (
          <React.Fragment>
            {/* Search, Filters, and Actions Row */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 2,
                pb: 2,
                alignItems: "center",
              }}
            >
              {/* Search Field */}
              <TextField
                size="small"
                placeholder="Search recycle bin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: "240px",
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    borderRadius: "10px",
                    bgcolor: "#fff",
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Type filter — native select so clear (X) never blocks the dropdown chevron */}
              <RecycleBinToolbarNativeSelect
                value={filterType}
                onChange={setFilterType}
                defaultValue="all"
                options={RECYCLE_BIN_TYPE_OPTIONS}
                minWidth={168}
                ariaLabel="Item type filter"
                title="Filter recycle bin by item type"
                clearAriaLabel="Clear type filter"
                clearTitle="Show all types"
                leftIcon={
                  <FilterListIcon sx={{ fontSize: 18, color: "#4A5565" }} />
                }
              />

              {/* Expiry range — same pattern (fixes chevron not opening when ≤ 7 Days is selected) */}
              <RecycleBinToolbarNativeSelect
                value={filterExpiryRange}
                onChange={setFilterExpiryRange}
                defaultValue="all"
                options={RECYCLE_BIN_EXPIRY_OPTIONS}
                minWidth={168}
                ariaLabel="Expiry range filter"
                title="Filter by days until permanent deletion"
                clearAriaLabel="Clear expiry filter"
                clearTitle="Show all expiry ranges"
                leftIcon={<CalendarToolbarIcon />}
              />

              {/* Deleted date — same control pattern as AI Detection dashboard table date (calendar + native select + X). */}
              <RecycleBinDeletedDateToolbarSelect
                value={filterDateRange}
                onChange={setFilterDateRange}
              />

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Empty Bin Button */}
              <Button
                onClick={handleEmptyBinClick}
                disabled={items.length === 0 && allRows.length === 0}
                startIcon={<DeleteForeverIcon />}
                sx={{
                  bgcolor: "#D32F2F",
                  border: "0.8px solid #D32F2F",
                  borderRadius: "10px",
                  color: "white",
                  textTransform: "none",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  height: 36,
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#B71C1C",
                    border: "0.8px solid #B71C1C",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    bgcolor: "#E5E7EB",
                    color: "#9CA3AF",
                    border: "0.8px solid #E5E7EB",
                  },
                }}
              >
                Empty Bin
              </Button>

              <ResetFiltersButton
                disabled={!hasActiveFilters}
                onClick={handleResetFilters}
              />
            </Box>

            {selectedRowKeys.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  // mx: 2,
                  mb: 1,
                  px: 2,
                  py: 1,
                  // borderRadius: "8px",
                  bgcolor: "#E3F2FD",
                  border: "1px solid #BBDEFB",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#1976D2",
                  }}
                >
                  {selectedRowKeys.length} selected
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <Tooltip title="Restore" {...GLOBAL_TOOLTIP_PROPS}>
                    <IconButton
                      size="small"
                      onClick={handleBulkToolbarRestore}
                      aria-label="Restore selected"
                      sx={{
                        color: "#1976D2",
                        "&:hover": { bgcolor: "rgba(25, 118, 210, 0.08)" },
                      }}
                    >
                      <RestoreIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete permanently" {...GLOBAL_TOOLTIP_PROPS}>
                    <IconButton
                      size="small"
                      onClick={handleBulkToolbarDelete}
                      aria-label="Permanently delete selected"
                      sx={{
                        color: "#D32F2F",
                        "&:hover": { bgcolor: "rgba(211, 47, 47, 0.08)" },
                      }}
                    >
                      <DeleteForeverIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear selection" {...GLOBAL_TOOLTIP_PROPS}>
                    <IconButton
                      size="small"
                      onClick={clearRowSelection}
                      aria-label="Clear selection"
                      sx={{
                        color: "#616161",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ) : null}

            {/* Table - fixed layout so header and body columns align */}
            <TableContainer
              sx={{ flex: 1, overflow: "auto", maxWidth: "100%", minHeight: 0 }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{ tableLayout: "fixed", minWidth: 936 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      onClick={() => handleSort("name")}
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: "22%",
                        minWidth: 160,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Item Name
                        {orderBy === "name" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("type")}
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: "12%",
                        minWidth: 96,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 0.5,
                        }}
                      >
                        Type
                        {orderBy === "type" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("deletedAt")}
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: "12%",
                        minWidth: 108,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 0.5,
                        }}
                      >
                        Deleted Date
                        {orderBy === "deletedAt" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("expiresAt")}
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: "12%",
                        minWidth: 112,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 0.5,
                        }}
                      >
                        Days Remaining
                        {orderBy === "expiresAt" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("originalLocation")}
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: "34%",
                        minWidth: 200,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Original Location
                        {orderBy === "originalLocation" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        ...TABLE_HEADER_STYLE,
                        width: 132,
                        minWidth: 132,
                        color:
                          hoveredRowKey != null || rowActionMenu != null
                            ? "#2932E5"
                            : "transparent",
                        transition: "color 0.2s",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: "100%",
                          py: 8,
                          minHeight: 320,
                          borderBottom: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            width: "100%",
                            minHeight: 280,
                          }}
                        >
                          <>
                            <DeleteForeverIcon
                              sx={{ fontSize: 64, color: "#E5E7EB" }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "16px",
                                color: "#9CA3AF",
                              }}
                            >
                              {searchTerm ||
                              filterType !== "all" ||
                              filterExpiryRange !== "all" ||
                              filterDateRange !== "all"
                                ? "No items match your filters"
                                : "No data is present."}
                            </Typography>
                          </>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedItems.map((item, index) => (
                      <TableRow
                        key={item.rowKey}
                        aria-selected={selectedKeySet.has(item.rowKey)}
                        onClick={() => toggleRowKey(item.rowKey)}
                        onContextMenu={(e) => {
                          if (
                            item.source !== "face" &&
                            item.source !== "store" &&
                            item.source !== "api"
                          ) {
                            return;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          setRowActionMenu({
                            anchor: "cursor",
                            top: e.clientY,
                            left: e.clientX,
                            rowItem: item,
                          });
                        }}
                        onMouseEnter={() => setHoveredRowKey(item.rowKey)}
                        onMouseLeave={() => setHoveredRowKey(null)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: selectedKeySet.has(item.rowKey)
                            ? "rgba(25, 118, 210, 0.08)"
                            : index % 2 === 0
                              ? "#FFFFFF"
                              : "#F9FAFB",
                          // Selected rows: keep the same background on hover (no flash).
                          "&:hover": {
                            bgcolor: selectedKeySet.has(item.rowKey)
                              ? "rgba(25, 118, 210, 0.08)"
                              : "rgba(0, 0, 0, 0.03)",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#212121",
                            width: "22%",
                            minWidth: 160,
                            verticalAlign: "middle",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name ?? ""}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            width: "12%",
                            minWidth: 96,
                            verticalAlign: "middle",
                          }}
                        >
                          <Chip
                            label={getTypeLabel(item.type)}
                            size="small"
                            sx={TYPE_BADGE_STYLE}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#212121",
                            width: "12%",
                            minWidth: 108,
                            verticalAlign: "middle",
                          }}
                        >
                          {item.deletedAt
                            ? item.deletedAt.toLocaleDateString("en-US", {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                              })
                            : ""}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            width: "12%",
                            minWidth: 112,
                            verticalAlign: "middle",
                          }}
                        >
                          <Chip
                            label={
                              item.expiresAt
                                ? `${getDaysRemaining(item.expiresAt)} days`
                                : "—"
                            }
                            size="small"
                            sx={{
                              ...DAYS_BADGE_STYLE,
                              ...(item.expiresAt &&
                              getDaysRemaining(item.expiresAt) <= 7
                                ? { bgcolor: "#FFEBEE", color: "#D32F2F" }
                                : {}),
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "13px",
                            color: "#4A5565",
                            width: "34%",
                            minWidth: 200,
                            verticalAlign: "middle",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip
                            title={
                              item.originalLocation?.trim()
                                ? item.originalLocation
                                : ""
                            }
                            disableHoverListener={
                              !item.originalLocation?.trim()
                            }
                            {...GLOBAL_TOOLTIP_PROPS}
                          >
                            <Typography
                              component="span"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontFamily: "inherit",
                                fontSize: "inherit",
                                color: "inherit",
                              }}
                            >
                              {item.originalLocation?.trim()
                                ? item.originalLocation
                                : "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="left"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: "10px",
                            py: "8px",
                            width: 132,
                            minWidth: 132,
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              justifyContent: "flex-start",
                              opacity: hoveredRowKey === item.rowKey ? 1 : 0,
                              transition: "opacity 0.2s",
                            }}
                          >
                            {(item.source === "face" ||
                              item.source === "store" ||
                              item.source === "api") && (
                              <>
                                <Tooltip
                                  title="Restore"
                                  {...GLOBAL_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRestoreClick(item);
                                    }}
                                    aria-label="Restore"
                                    sx={{
                                      color: "#2932E5",
                                      p: 0.5,
                                      "&:hover": { bgcolor: "#E6E7FF" },
                                    }}
                                  >
                                    <RestoreIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Delete permanently"
                                  {...GLOBAL_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(item);
                                    }}
                                    aria-label="Delete permanently"
                                    sx={{
                                      color: "#D32F2F",
                                      p: 0.5,
                                      "&:hover": {
                                        bgcolor: "rgba(211, 47, 47, 0.08)",
                                      },
                                    }}
                                  >
                                    <DeleteForeverIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="More actions"
                                  {...GLOBAL_TOOLTIP_PROPS}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRowMenuOpenFromButton(e, item);
                                    }}
                                    aria-label="More actions"
                                    sx={{
                                      color: "#4A5565",
                                      p: 0.5,
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Footer */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                borderTop: "1px solid #E5E7EB",
                bgcolor: "#F2F2F2",
                minHeight: "52px",
              }}
            >
              <TablePagination
                component="div"
                count={filteredAndSortedItems.length}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
                sx={{
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      color: "#4A5565",
                      mb: 0,
                    },
                }}
              />
            </Box>
          </React.Fragment>
        )}
      </Box>

      {/* Row context menu — right-click on row or ⋮ (compact white pop-up) */}
      <Menu
        open={Boolean(rowActionMenu)}
        onClose={handleRowMenuClose}
        anchorReference={
          rowActionMenu?.anchor === "cursor" ? "anchorPosition" : "anchorEl"
        }
        anchorPosition={
          rowActionMenu?.anchor === "cursor"
            ? { top: rowActionMenu.top, left: rowActionMenu.left }
            : undefined
        }
        anchorEl={
          rowActionMenu?.anchor === "element" ? rowActionMenu.el : undefined
        }
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            onContextMenu: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            },
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: "#fff",
            borderRadius: "10px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.14)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            minWidth: 212,
            py: 0.5,
            mt: rowActionMenu?.anchor === "element" ? 0.5 : 0,
            overflow: "hidden",
          },
        }}
      >
        <MenuItem
          onClick={() => handleRestoreClick()}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            color: "#2932E5",
            px: 2,
            py: 1.25,
            gap: 1.25,
            borderRadius: 0,
            "&:hover": { bgcolor: "rgba(41, 50, 229, 0.06)" },
          }}
        >
          <RestoreIcon sx={{ fontSize: 20, color: "#2932E5" }} />
          Restore
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteClick()}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            color: "#D32F2F",
            px: 2,
            py: 1.25,
            gap: 1.25,
            borderRadius: 0,
            "&:hover": { bgcolor: "rgba(211, 47, 47, 0.06)" },
          }}
        >
          <DeleteForeverIcon sx={{ fontSize: 20, color: "#D32F2F" }} />
          Delete Permanently
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({
            open: false,
            type: "restore",
            bulkRows: undefined,
          })
        }
        PaperProps={{
          sx: {
            borderRadius: "8px",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
            color: "#212121",
          }}
        >
          {confirmDialog.type === "restore" && "Restore Item"}
          {confirmDialog.type === "bulkRestore" && "Restore Items"}
          {confirmDialog.type === "delete" && "Delete Permanently"}
          {confirmDialog.type === "bulkDelete" && "Delete Items Permanently"}
          {confirmDialog.type === "empty" && "Empty Recycle Bin"}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              lineHeight: 1.5,
            }}
          >
            {confirmDialog.type === "restore" &&
              "Are you sure you want to restore this item? It will be moved back to its original location."}
            {confirmDialog.type === "bulkRestore" &&
              `Are you sure you want to restore ${confirmDialog.bulkRows?.length ?? 0} selected items? They will be moved back to their original locations.`}
            {confirmDialog.type === "delete" &&
              "Are you sure you want to permanently delete this item? This action cannot be undone."}
            {confirmDialog.type === "bulkDelete" &&
              `Are you sure you want to permanently delete ${confirmDialog.bulkRows?.length ?? 0} selected items? This action cannot be undone.`}
            {confirmDialog.type === "empty" &&
              `Are you sure you want to empty the recycle bin? All ${allRows.length} items will be permanently deleted. This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                type: "restore",
                bulkRows: undefined,
              })
            }
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              px: 2,
              py: 0.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={
              restoreFaceLoading || deleteFaceLoading || emptyBinLoading
            }
            sx={{
              bgcolor:
                confirmDialog.type === "restore" ||
                confirmDialog.type === "bulkRestore"
                  ? "#2932E5"
                  : "#D32F2F",
              color: "white",
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              px: 2,
              py: 0.5,
              borderRadius: "8px",
              border: `0.8px solid ${
                confirmDialog.type === "restore" ||
                confirmDialog.type === "bulkRestore"
                  ? "#2932E5"
                  : "#D32F2F"
              }`,
              "&:hover": {
                bgcolor:
                  confirmDialog.type === "restore" ||
                  confirmDialog.type === "bulkRestore"
                    ? "#1F25B0"
                    : "#B71C1C",
              },
            }}
          >
            {confirmDialog.type === "restore" &&
              (restoreFaceLoading ? "Restoring..." : "Restore")}
            {confirmDialog.type === "bulkRestore" &&
              (restoreFaceLoading ? "Restoring..." : "Restore")}
            {confirmDialog.type === "delete" &&
              (deleteFaceLoading ? "Deleting..." : "Delete Permanently")}
            {confirmDialog.type === "bulkDelete" &&
              (deleteFaceLoading ? "Deleting..." : "Delete Permanently")}
            {confirmDialog.type === "empty" &&
              (emptyBinLoading ? "Emptying..." : "Empty Bin")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
